<template>
  <div>
    <!-- Checkout form: conditional wrapper based on order duplication prevention -->
    <template v-if="campaign.is_prevent_order_duplication">
      <!-- Panel 1: Shopper Verification -->
      <v-expansion-panel
        v-model="localPanel1"
        class="expansion-panel"
      >
        <v-expansion-panel-title class="pa-2">
            <div class="d-flex">
              <div
                class="d-flex align-center panel-header-layout"
              >
                <h3 class="mb-1 panel-header">
                  <img
                    v-if="localIsEmailConfirmed"
                    src="@/assets/green_check.png"
                    style="width: 25px"
                    alt=""
                    class="mr-2"
                  />
                  <img
                    v-else
                    src="@/assets/number1_selected.png"
                    style="width: 25px"
                    alt=""
                    class="mr-2"
                  />
                  Shopper Verification
                </h3>
                <span
                  v-if="localIsEmailConfirmed"
                  class="edit-text mr-4"
                  @click="editCompanyEmail"
                  >Edit</span
                >
              </div>
            </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="pa-2">
          <v-divider class="mt-1" />
          <v-card class="panel-content">
            <div v-if="!localIsEmailConfirmed">
              <v-text-field
                ref="email"
                label="Email *"
                v-model="localCheckoutFields['companyEmail']"
                :rules="getCompanyEmailRules()"
                data-testid="co-email"
                autocomplete="off"
              />
              <div class="d-flex align-center">
                <v-spacer />
                <v-btn
                  color="primary"
                  variant="flat"
                  :disabled="
                    validateEmailDomain(
                      localCheckoutFields['companyEmail']
                    ) === domainIncorrectMessage
                  "
                  @click="confirmCompanyEmail()"
                  style="text-transform: none"
                  >Continue</v-btn
                >
              </div>
            </div>
            <div v-else>
              <div class="md-light">Email</div>
              <div class="md-body">
                {{ localCheckoutFields["companyEmail"] }}
              </div>
            </div>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Panel 2: Checkout Info (expansion panel variant) -->
      <v-expansion-panel
        class="mt-3 expansion-panel"
        v-model="localPanel2"
      >
        <v-expansion-panel-title
          :disabled="!localIsEmailConfirmed"
          class="pa-2"
        >
            <div class="d-flex align-center mt-1">
              <img
                v-if="localIsEmailConfirmed"
                src="@/assets/number2_selected.png"
                style="width: 25px"
                alt=""
                class="mr-2"
              />
              <img
                v-else
                src="@/assets/number2_unselected.png"
                style="width: 25px"
                alt=""
                class="mr-2"
              />
              <div class="panel-header">Checkout Info</div>
            </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="pa-2">
          <v-divider class="mt-1" />

          <v-card class="panel-content">
            <checkout-fields-form
              :campaign="campaign"
              :checkoutFieldsResult="localCheckoutFields"
              :customCheckoutFieldsResult="localCustomCheckoutFields"
              :customCheckoutFields="customCheckoutFields"
              :checkoutCustomDropdowns="checkoutCustomDropdowns"
              :shipToShopperAddress="localShipToShopperAddress"
              :shopperLocation="shopperLocation"
              :countriesAndCodes="countriesAndCodes"
              :coupon="localCoupon"
              :couponAmount="localCouponAmount"
              :stripePending="stripePending"
              :couponChecking="couponChecking"
              :salesTaxLoading="salesTaxLoading"
              :storeShipTo="storeShipTo"
              :storeShipToState="storeShipToState"
              :showEmailField="false"
              @update:shipToShopperAddress="localShipToShopperAddress = $event"
              @update:coupon="localCoupon = $event"
              @update:couponAmount="localCouponAmount = $event"
              @location-changed="$emit('location-changed')"
              @apply-coupon="$emit('apply-coupon')"
              @form-valid-changed="shopperLocationFormValid = $event"
              @update:costomDropdownsValid="localCostomDropdownsValid = $event"
            />
            <store-email-verification
              v-if="campaign.requires_verified_email"
              :store-id="campaign.id"
              :email="localCheckoutFields['companyEmail'] || ''"
              @verified="onEmailVerified"
            />
            <div class="d-flex justify-end align-center">
              <v-spacer />
              <v-btn
                size="large"
                @click="$emit('place-order-clicked')"
                color="primary"
                :disabled="
                  placeOrderButtonDisabled ||
                    emailVerificationPending ||
                    (isCard && stripe == null) ||
                    isCheckoutProcess
                "
                data-testid="co-order"
                variant="flat"
                style="text-transform: none"
                ><v-progress-circular
                  v-if="salesTaxLoading || calculatingShipping"
                  :indeterminate="true"
                  color="white"
                  size="25"
                  width="2"
                />
                <div v-else>Continue to Checkout</div></v-btn
              >
            </div>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </template>

    <!-- Non-duplication-prevention path: flat card -->
    <template v-else>
      <v-card elevation="1" class="pa-2 expansion-panel">
        <v-card-title class="mb-2">
          <div class="d-flex align-center">
            <img
              src="@/assets/number1_selected.png"
              style="width: 25px"
              alt=""
              class="mr-2"
            />
            <div class="panel-header">Checkout Info</div>
          </div>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <checkout-fields-form
            :campaign="campaign"
            :checkoutFieldsResult="localCheckoutFields"
            :customCheckoutFieldsResult="localCustomCheckoutFields"
            :customCheckoutFields="customCheckoutFields"
            :checkoutCustomDropdowns="checkoutCustomDropdowns"
            :shipToShopperAddress="localShipToShopperAddress"
            :shopperLocation="shopperLocation"
            :countriesAndCodes="countriesAndCodes"
            :coupon="localCoupon"
            :couponAmount="localCouponAmount"
            :stripePending="stripePending"
            :couponChecking="couponChecking"
            :salesTaxLoading="salesTaxLoading"
            :storeShipTo="storeShipTo"
            :storeShipToState="storeShipToState"
            :showEmailField="true"
            @update:shipToShopperAddress="localShipToShopperAddress = $event"
            @update:coupon="localCoupon = $event"
            @update:couponAmount="localCouponAmount = $event"
            @location-changed="$emit('location-changed')"
            @apply-coupon="$emit('apply-coupon')"
            @form-valid-changed="shopperLocationFormValid = $event"
            @update:costomDropdownsValid="localCostomDropdownsValid = $event"
          />
        </v-card-text>
        <div v-if="campaign.requires_verified_email" class="px-4">
          <store-email-verification
            :store-id="campaign.id"
            :email="localCheckoutFields['companyEmail'] || ''"
            @verified="onEmailVerified"
          />
        </div>
        <div class="d-flex justify-end align-center pa-4">
          <v-spacer />
          <v-btn
            size="large"
            @click="$emit('place-order-clicked')"
            color="primary"
            :disabled="
              placeOrderButtonDisabled ||
                emailVerificationPending ||
                (isCard && stripe == null) ||
                isCheckoutProcess
            "
            data-testid="co-order"
            variant="flat"
            style="text-transform: none"
            ><v-progress-circular
              v-if="salesTaxLoading || calculatingShipping"
              :indeterminate="true"
              color="white"
              size="25"
              width="2"
            />
            <div v-else>Continue to Checkout</div></v-btn
          >
        </div>
      </v-card>
    </template>

    <!-- Payment / Confirm Order dialog.
         `persistent`: a click on the backdrop (or Esc) must not dismiss a
         dialog the shopper is typing a card into. The X button is the way out. -->
    <v-dialog
      v-model="localCcDialog"
      persistent
      :max-width="dialogMaxWidth"
      :fullscreen="isMobile()"
    >
      <v-card>
        <v-card-title class="text-h5">
          <div class="d-flex align-center">
            <!--
              Testids distinguish WHICH dialog opened. Without them the
              card-taking and no-card variants are indistinguishable to
              automation -- both are the same v-dialog, and the only
              difference was untestable prose. The AdventHealth bug was
              precisely a store getting the wrong one of these two, so the
              distinction needs to be assertable.
            -->
            <div v-if="showCCInfo" data-testid="co-dialog-payment">
              Review &amp; Enter Payment
            </div>
            <div v-else data-testid="co-dialog-confirm-only">
              Confirm Order
            </div>
            <v-spacer />
            <v-btn
              icon
              variant="plain"
              :disabled="stripePending"
              data-testid="co-dialog-close"
              @click="localCcDialog = false"
            >
              <v-icon icon="close" />
            </v-btn>
          </div>
        </v-card-title>

        <v-divider />
        <!-- flex-wrap: on a narrow (fullscreen mobile) dialog the columns
             stack instead of squeezing the totals column. -->
        <div
          :class="stackColumns ? 'flex-column' : 'd-flex flex-wrap'"
          class="d-flex"
          data-testid="co-dialog-columns"
        >
          <v-card elevation="0" class="pa-4">
            <div class="d-flex">
              <v-card elevation="0" width="350" max-width="100%">
                <v-row>
                  <!-- Every figure in this column comes from `figures`: the
                       SERVER's breakdown (`checkout_quote.totals`) when the
                       cart has one for its current inputs, the cart's own
                       arithmetic otherwise, and then the Total says
                       "(estimated)". This is the LAST screen before a card
                       is entered; Bug Intake #481, cartTotalsDisplay.js. -->
                  <!-- Items -->
                  <v-col cols="8"> Items: </v-col>
                  <v-col cols="4" class="text-no-wrap">
                    ${{ figures.items.toFixed(2) }}
                  </v-col>
                  <!-- Tax & Processing Fee: -->

                  <!-- "Tax and Fees:", Jake's wording (2026-09-15). Was
                       "Tax & Processing Fee", which named exactly two
                       components and so could not honestly cover the
                       platform fee now summed into this line when the
                       shopper bears it. -->
                  <v-col cols="8" v-if="campaign.consolidate_tax_fee">
                    Tax and Fees:
                  </v-col>
                  <v-col
                    cols="4" class="text-no-wrap"
                    v-if="campaign.consolidate_tax_fee"
                    data-testid="cart-checkout-tax-and-fee"
                  >
                    <span
                      v-if="
                        !figures.fromServer &&
                          (salesTaxState === 'not-calculated' ||
                            isCartProcessingFeeTBD)
                      "
                      >Calculated at checkout</span
                    >
                    <span
                      v-else-if="
                        !figures.fromServer && salesTaxState === 'calculating'
                      "
                      >Calculating Sales Tax</span
                    >
                    <!-- `cartProcessingFee` ALONE, not `.add(appFee)` --
                         see the standalone Processing Fee row below.
                         `consolidatedTaxAndFees` adds the PLATFORM fee on
                         top, and only when the shopper bears it; that is
                         what makes this one line foot to the Total. -->
                    <span v-else>${{ figures.taxAndFees.toFixed(2) }}</span>
                  </v-col>
                  <!-- Tax  -->
                  <v-col
                    cols="8"
                    v-if="!campaign.consolidate_tax_fee && collectSalesTax"
                  >
                    Sales Tax:
                  </v-col>
                  <v-col
                    cols="4" class="text-no-wrap"
                    v-if="!campaign.consolidate_tax_fee && collectSalesTax"
                    data-testid="cart-checkout-tax"
                  >
                    <span
                      v-if="
                        !figures.fromServer && salesTaxState === 'not-calculated'
                      "
                      >Calculated at checkout</span
                    >
                    <span
                      v-else-if="
                        !figures.fromServer && salesTaxState === 'calculating'
                      "
                      >Calculating Sales Tax</span
                    >
                    <span
                      v-else-if="
                        salesTaxState === 'not-collected' &&
                          figures.salesTax === 0
                      "
                      >N/A</span
                    >
                    <span v-else>${{ figures.salesTax.toFixed(2) }}</span>
                  </v-col>

                  <!-- Coupon  -->
                  <v-col cols="8" v-if="localCouponAmount > 0"> Coupon: </v-col>
                  <v-col
                    cols="4" class="text-no-wrap"
                    v-if="localCouponAmount > 0 && figures.fromServer"
                  >
                    -${{ figures.couponDiscount.toFixed(2) }}
                  </v-col>
                  <v-col
                    cols="4" class="text-no-wrap"
                    v-if="localCouponAmount > 0 && !figures.fromServer"
                  >
                    -{{
                      couponType === "$"
                        ? couponType + parseFloat(localCouponAmount).toFixed(2)
                        : ""
                    }}
                    {{
                      couponType === "%" ? localCouponAmount + couponType : ""
                    }}
                  </v-col>
                  <!-- Shipping  -->
                  <v-col cols="8" v-if="campaign.shipping_cost_enabled">
                    Shipping Cost:
                  </v-col>
                  <v-col
                    cols="4" class="text-no-wrap"
                    v-if="campaign.shipping_cost_enabled"
                    data-testid="cart-checkout-shipping-cost"
                  >
                    <span v-if="isShippingCostTBD && !figures.fromServer">TBD</span
                    ><span v-else>${{ figures.shipping.toFixed(2) }}</span>
                  </v-col>
                  <!-- Processing Fee.
                       Rendered ABOVE the subtotal, because the subtotal
                       INCLUDES it (Jake, 2026-09-14). Both surfaces are fed
                       the SAME `subTotal` prop from StorefrontCart, so the
                       fee is in that figure whether or not this template
                       moves; leaving the row below the line would show the
                       fee twice and stop the column footing. -->
                  <v-col cols="8" v-if="showProcessingFeeRow">
                    Processing Fee:
                  </v-col>
                  <v-col
                    cols="4" class="text-no-wrap"
                    v-if="showProcessingFeeRow"
                    data-testid="cart-checkout-processing-fee"
                  >
                    <!-- THE DISPLAYED FEE MUST EQUAL THE CHARGED FEE, and
                         this is the LAST screen before a card is entered.
                         Was `cartProcessingFee.add(appFee)`, correct only
                         while `appFee` was added to the charge; it is the
                         legacy 3.95% duplicating the canonical platform fee
                         and is no longer charged. Pinned by
                         __tests__/cart-checkout-display-fee-equals-charged-fee.spec.js. -->
                    <span v-if="isCartProcessingFeeTBD && !figures.fromServer"
                      >TBD</span
                    >
                    <span v-else>${{ figures.processingFee.toFixed(2) }}</span>
                  </v-col>
                  <!-- Service Fee -- the ThreadFlow platform fee, as its OWN
                       line (Jake, 2026-09-15). THIS IS THE SURFACE IN JAKE'S
                       SCREENSHOT: order AT-2026-01033 rendered Items 17.02 +
                       Processing 0.79 = Subtotal 17.81 above a Total of
                       18.48, with nothing on screen accounting for 67c. The
                       charge was right; the dialog simply had no line for
                       the platform fee, per a 2026-07-23 decision to hide it
                       from shoppers that Jake has now overridden for the
                       shopper-bears case.
                       Shown only when consolidation is off AND the shopper
                       bears the fee, and ABOVE the subtotal because the
                       subtotal INCLUDES it. When the distributor absorbs it
                       there is no row -- the shopper is not charged it, and
                       showing it would leak ThreadFlow's margin. -->
                  <v-col
                    cols="8"
                    v-if="!campaign.consolidate_tax_fee && figures.serviceFee > 0"
                  >
                    Service Fee:
                  </v-col>
                  <v-col
                    cols="4" class="text-no-wrap"
                    v-if="!campaign.consolidate_tax_fee && figures.serviceFee > 0"
                    data-testid="cart-checkout-service-fee"
                  >
                    ${{ figures.serviceFee.toFixed(2) }}
                  </v-col>
                  <!-- Highest-priced item free (https://trello.com/c/T756dpsG).
                       Same line, same wording as CartSummary, because this is
                       the LAST screen before a shopper enters a card and an
                       unexplained gap between sub-total and total reads as a
                       pricing bug. It read as exactly that on the live
                       AdventHealth store: $432 sub-total, $297 total, nothing
                       on screen accounting for the missing $135.
                       ABOVE the Subtotal (#481): the Subtotal already has it
                       taken off (utils/cartSubTotal.js; the server's
                       breakdown orders it the same way), so below the line
                       it read as a second deduction. -->
                  <v-col cols="8" v-if="figures.freeItem > 0">
                    Free item:
                  </v-col>
                  <v-col
                    cols="4" class="text-no-wrap"
                    v-if="figures.freeItem > 0"
                    data-testid="cart-free-item-discount"
                  >
                    -${{ figures.freeItem.toFixed(2) }}
                  </v-col>
                  <!-- Stripe's $0.50 minimum on a tiny card order, shown as
                       what the clamp added so the column foots. -->
                  <v-col cols="8" v-if="figures.minimumCharge > 0">
                    Minimum charge:
                  </v-col>
                  <v-col
                    cols="4" class="text-no-wrap"
                    v-if="figures.minimumCharge > 0"
                    data-testid="cart-checkout-minimum-charge"
                  >
                    ${{ figures.minimumCharge.toFixed(2) }}
                  </v-col>
                  <!-- Subtotal  -->
                  <v-col cols="8"> Subtotal: </v-col>
                  <v-col cols="4" class="text-no-wrap" data-testid="cart-checkout-subtotal">
                    ${{ figures.subtotal > 0 ? figures.subtotal.toFixed(2) : "0.00" }}
                  </v-col>
                  <!-- Allowance  -->
                  <v-col v-if="figures.allowance > 0" cols="8"> Allowance: </v-col>
                  <v-col v-if="figures.allowance > 0" cols="4" class="text-no-wrap">
                    -${{ figures.allowance.toFixed(2) }}
                  </v-col>

                  <!-- Total  -->
                  <v-col cols="8" class="text-primary md-subheader-no-spacing">
                    Total:
                  </v-col>
                  <v-col
                    cols="4"
                    class="text-no-wrap text-primary md-subheader-no-spacing co-total-value"
                    data-testid="cart-grand-total"
                  >
                    ${{
                      figures.total > 0 ? figures.total.toFixed(2) : "0.00"
                    }}
                    <span
                      v-if="!figures.fromServer"
                      class="text-medium-emphasis"
                      data-testid="cart-grand-total-estimate"
                    >(estimated)</span>
                  </v-col>
                </v-row>
              </v-card>
            </div>
          </v-card>
          <!-- TENDER SELECTION (Unit 4, D-01). Rendered only when the
               SERVER says this store may offer Net Checkout; a store that
               does not offer it is card-only and shows no control at all
               rather than a disabled one. Card is preselected. -->
          <v-divider vertical v-if="!isMobile() && netCheckoutAvailable && !stackColumns" />
          <v-card
            elevation="0"
            class="pa-4"
            v-if="netCheckoutAvailable"
            data-testid="co-tender-group"
          >
            <div class="mb-2">Payment Method</div>
            <v-radio-group v-model="selectedTender" hide-details>
              <!-- The label is a testid'd span so a scenario can press what
                   a shopper presses: v-radio puts its own data-testid on the
                   hidden input, which a click does not reliably toggle. -->
              <v-radio value="card" data-testid="co-tender-card">
                <template #label>
                  <span data-testid="co-tender-card-label">Credit Card</span>
                </template>
              </v-radio>
              <v-radio value="net_checkout" data-testid="co-tender-net">
                <template #label>
                  <span data-testid="co-tender-net-label">Net Checkout</span>
                </template>
              </v-radio>
            </v-radio-group>
            <!-- CK-06 copy. States plainly that nothing is collected now: the
                 decoration, platform fee and shipping are all billed on the
                 store owner's 1st/15th invoice after the order ships (Jake,
                 2026-10-28, Bug Intake #563). -->
            <v-card-text
              v-if="isNetCheckout"
              class="textline px-0"
              data-testid="co-tender-net-note"
            >
              No card is collected now. Decoration, the platform fee and
              shipping are added to the store owner's 1st/15th invoice after
              the order ships.
            </v-card-text>
            <!-- The store's Net PIN (once per session) and the terms (every
                 Net order). Mounted only while Net is chosen, so the card
                 path never sees it. No data-testid on this tag: the
                 component's own testids must not be clobbered. -->
            <net-checkout-credential
              v-if="isNetCheckout"
              ref="netCredential"
              :store-id="campaign.id"
              @state="onNetCredentialState"
            />
          </v-card>
          <v-divider vertical v-if="!isMobile() && showCCInfo" />
          <v-card elevation="0" v-show="showCCInfo">
            <v-card-text class="textline"
              >All transactions are secure and encrypted.</v-card-text
            >
            <v-col
              v-if="campaign.checkout_fields.includes('First Name')"
              cols="12"
              md="12"
              class="mx-3 px-4"
            >
              <v-text-field
                label="First Name *"
                v-model="localCheckoutFields['firstName']"
                data-testid="co-first"
                autocomplete="off"
              />
            </v-col>
            <v-col
              v-if="campaign.checkout_fields.includes('Last Name')"
              cols="12"
              md="12"
              class="mx-3 px-4"
            >
              <v-text-field
                label="Last Name *"
                v-model="localCheckoutFields['lastName']"
                data-testid="co-last"
                autocomplete="off"
              />
            </v-col>

            <v-col cols="12" class="mt-5 mb-5 d-flex justify-center" v-if="stripePending">
              <v-progress-circular
                :indeterminate="true"
                color="primary"
                size="32"
                width="4"
              />
            </v-col>
            <v-container class="form-row">
              <div class="cell stripe-div stripe-form" id="stripe-form-id">
                <form>
                  <div class="row">
                    <div class="field">
                      <!-- data-testid so a ui-runner scenario can assert the
                           card form actually MOUNTED. The Stripe Elements
                           iframe itself is out of reach (`fill_stripe` has no
                           executor, GH #3177), but "the mount point exists and
                           Stripe.js loaded without console errors" is
                           expressible and is what would break if the
                           platform-scoped instance failed to load. -->
                      <div
                        id="stripe-form-card-number"
                        data-testid="co-card-number"
                        class="input empty"
                      ></div>
                      <label
                        for="stripe-form-card-number"
                        data-tid="elements.form.card_number_label"
                        >Card number</label
                      >
                      <div class="baseline"></div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="field half-width">
                      <div
                        id="stripe-form-card-expiry"
                        class="input empty"
                      ></div>
                      <label
                        for="stripe-form-card-expiry"
                        data-tid="elements.form.card_expiry_label"
                        >Expiration</label
                      >
                      <div class="baseline"></div>
                    </div>
                    <div class="field half-width">
                      <div
                        id="stripe-form-card-cvc"
                        class="input empty"
                      ></div>
                      <label
                        for="stripe-form-card-cvc"
                        data-tid="elements.form.card_cvc_label"
                        >CVC</label
                      >
                      <div class="baseline"></div>
                    </div>
                  </div>
                </form>
              </div>
              <div id="card-errors" role="alert"></div>
            </v-container>
          </v-card>
        </div>
        <v-divider />
        <v-card-actions>
          <v-btn
            color="primary"
            variant="flat"
            :disabled="
              placeOrderButtonDisabled ||
                (isCard && !isNetCheckout && stripe == null) ||
                (isNetCheckout && !netCredentialReady) ||
                isCheckoutProcess ||
                stripePending ||
                (campaign.shipping_cost_enabled && calculatingShipping)
            "
            @click="confirmPlaceOrder()"
            data-testid="co-place-order"
            style="width: 100%; text-transform: none"
            >Place Order</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notification dialog -->
    <v-dialog v-model="showNotificationDialog" max-width="600">
      <v-card>
        <v-card-title class="text-h5">{{
          notificationHeader
        }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text data-testid="valid-coupon-dialog">{{
          notificationBody
        }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            data-testid="close-valid-coupon-dialog"
            color="primary"
            variant="text"
            @click="showNotificationDialog = false"
            >{{ notificationBtn }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import currency from "currency.js";
import helpers from "@/mixins/helpers";
import rules from "@/mixins/rules";
import store from "@/store";
import { loadStripe } from "@stripe/stripe-js";
import { buildTimePublishableKey } from "@/services/stripePublishableKey";
import {
  isAccountNotReady,
  accountNotReadyMessage,
  isOrderNotPayable,
  orderNotPayableMessage,
} from "@/services/stripeService";
import StripeEvent from "@/models/StripeEvent";
import CartShippingForm from "./CartShippingForm.vue";
import CartSavedAddressPicker from "./CartSavedAddressPicker.vue";
import CartShipToDestination from "./CartShipToDestination.vue";
import {
  departmentPickerMode,
  storeResolvesDestination,
} from "@/utils/campaign";
import StoreEmailVerification from "./StoreEmailVerification.vue";
import NetCheckoutCredential from "./NetCheckoutCredential.vue";
import { cartDisplayFigures } from "./cartTotalsDisplay";
import { NET_PIN_REQUIRED_CODE } from "@/interceptors/authResponseInterceptor";

/**
 * Inline sub-component: renders the shared checkout fields form.
 * Used in both the expansion-panel and flat-card paths to eliminate duplication.
 *
 * NOT A DUPLICATE `watch:` BLOCK -- do not "fix" it.
 * =================================================
 * This file contains TWO component definitions: this one, and the default
 * export further down. Each therefore has its own `watch:`, `data:`,
 * `computed:` and `methods:` -- and because this one is defined ABOVE
 * `export default`, its option blocks look at a glance like top-level
 * siblings of the other component's. They are not. Two legitimate option
 * sets, not a duplicate object key that silently overwrites.
 *
 * This has already been mis-read once, during the investigation of the
 * "Continue to Checkout stays disabled" bug (2026-09-08), and the proposed
 * "fix" -- merging the two `watch:` blocks -- would have broken the
 * form-validity chain outright, since each component watches its OWN
 * `shopperLocationFormValid` and re-emits upward. The chain is deliberate:
 *
 *   CartShippingForm  --form-valid-changed-->  CheckoutFieldsForm (here)
 *   CheckoutFieldsForm --form-valid-changed--> CartCheckout (default export)
 *   CartCheckout       --form-valid-changed--> StorefrontCart
 *
 * If you are reading this because two `watch:` blocks look wrong: check
 * which component each one belongs to first.
 */
const CheckoutFieldsForm = {
  name: "CheckoutFieldsForm",

  mixins: [rules],

  components: {
    "cart-shipping-form": CartShippingForm,
    "cart-saved-address-picker": CartSavedAddressPicker,
    "cart-ship-to-destination": CartShipToDestination,
  },

  props: {
    campaign: { type: Object, required: true },
    checkoutFieldsResult: { type: Object, required: true },
    customCheckoutFieldsResult: { type: Object, required: true },
    customCheckoutFields: { type: Array, default: () => [] },
    checkoutCustomDropdowns: { type: Array, default: () => [] },
    shipToShopperAddress: { type: Boolean, default: false },
    shopperLocation: { type: Object, required: true },
    countriesAndCodes: { type: Array, default: () => [] },
    coupon: { type: String, default: "" },
    couponAmount: { type: Number, default: 0 },
    stripePending: { type: Boolean, default: false },
    // A coupon lookup is in flight in the cart (disables Apply Coupon).
    couponChecking: { type: Boolean, default: false },
    salesTaxLoading: { type: Boolean, default: false },
    // The server-resolved destination of a company / department store
    // (`checkout_quote` -> `ship_to`) and the quote's progress. Unused on a
    // shopper-address store, where the shopper enters the destination.
    storeShipTo: { type: Object, default: null },
    storeShipToState: { type: String, default: "idle" },
    showEmailField: { type: Boolean, default: false },
  },

  emits: [
    "update:shipToShopperAddress",
    "update:coupon",
    "update:couponAmount",
    "location-changed",
    "apply-coupon",
    "form-valid-changed",
    "update:costomDropdownsValid",
  ],

  data() {
    return {
      shopperLocationFormValid: false,
      costomDropdownsValid: true,
    };
  },

  computed: {
    /**
     * Offer the coupon field when the store has coupons to apply. Read from
     * the store read's `has_coupons` (coupons enabled AND at least one active
     * coupon): the shopper payload no longer carries the coupon list, and a
     * code is resolved one at a time by the server
     * (`POST /storefront/stores/<id>/coupon`).
     */
    showCouponField() {
      return this.campaign?.has_coupons === true;
    },

    /** Apply Coupon is off while a payment is pending or a lookup is in flight. */
    applyCouponDisabled() {
      return Boolean(this.stripePending || this.couponChecking);
    },
    localShipToShopperAddress: {
      get() { return this.shipToShopperAddress; },
      set(val) { this.$emit("update:shipToShopperAddress", val); },
    },
    localCoupon: {
      get() { return this.coupon; },
      set(val) { this.$emit("update:coupon", val); },
    },
    couponEnteredButNotApplied() {
      return this.coupon && !this.couponAmount;
    },
    // Client Portal v2 (PR C): typed client-department mapping surfaced by
    // getCampaignById. Drives the 0 / 1 / N picker rule. Always an array.
    clientDepartments() {
      return this.campaign.client_departments || [];
    },
    // 'none' | 'auto' | 'required' -- the picker renders only for 'required'.
    departmentPickerMode() {
      return departmentPickerMode(this.clientDepartments);
    },
    // A company / department store: the server decides where the order
    // ships, and the form shows that destination instead of asking for one.
    storeResolvesDestination() {
      return storeResolvesDestination(this.campaign);
    },
  },

  watch: {
    shopperLocationFormValid(newVal) {
      this.$emit("form-valid-changed", newVal);
    },
    costomDropdownsValid(newVal) {
      this.$emit("update:costomDropdownsValid", newVal);
    },
  },

  methods: {
    changedCompanyLocation() {
      this.$emit("location-changed");
    },
    applyCoupon() {
      this.$emit("apply-coupon");
    },
    onCouponKeyup() {
      this.$emit("update:couponAmount", 0);
    },
    onShippingFormValidChanged(valid) {
      this.shopperLocationFormValid = valid;
    },

    /**
     * Copy a picked saved address into the cart's shipping form.
     *
     * Assigns FIELD BY FIELD onto the existing `shopperLocation` object
     * rather than replacing it. `shopperLocation` is the parent's own `data`
     * object, passed down and shared by reference with CartShippingForm's
     * `localValue`; swapping in a new object would leave the form bound to
     * the old one, so the shopper would see their picked address vanish.
     * Mutating in place is also what keeps the country/region watchers --
     * and therefore the validity signal -- firing normally.
     */
    async onSavedAddressSelected(address) {
      if (!address) return;

      for (const field of [
        "country",
        "region",
        "street_and_number",
        "suite_unit_etc",
        "city",
        "zip_code",
      ]) {
        this.shopperLocation[field] = address[field] ?? "";
      }

      // Re-assert `region` AFTER Vue has flushed the watchers.
      //
      // CartShippingForm watches `localValue.country` and clears `region`
      // when the country genuinely changes -- correct on its own terms, since
      // a US state is meaningless under another country. That watcher runs
      // ASYNCHRONOUSLY, so it fires after this whole loop has finished and
      // wipes the state we just wrote. Ordering the assignments does not help
      // for the same reason.
      //
      // Measured before this guard: prefilling a US saved address onto a form
      // whose country was "CA" produced
      //   {"city":"Austin","country":"US","region":"", ...}
      // -- every field correct except the state, giving the shopper an
      // address that looks complete and will never validate.
      //
      // The re-assert is a no-op in the common case (same country, watcher
      // never clears anything), and correct in the cross-country case.
      const region = address.region ?? "";
      await this.$nextTick();
      if (this.shopperLocation.region !== region) {
        this.shopperLocation.region = region;
      }
    },
  },

  template: `
    <v-row>
      <v-col
        v-if="campaign.checkout_fields.includes('First Name')"
        cols="12"
        md="12"
        class="pb-0"
      >
        <v-text-field
          label="First Name *"
          v-model="checkoutFieldsResult['firstName']"
          data-testid="co-first"
          autocomplete="off"
        />
      </v-col>
      <v-col
        v-if="campaign.checkout_fields.includes('Last Name')"
        cols="12"
        md="12"
        class="py-0"
      >
        <v-text-field
          label="Last Name *"
          v-model="checkoutFieldsResult['lastName']"
          data-testid="co-last"
          autocomplete="off"
        />
      </v-col>
      <v-col
        v-if="showEmailField && campaign.checkout_fields.includes('Company Email')"
        cols="12"
        md="12"
        class="py-0"
      >
        <v-text-field
          ref="email"
          label="Email *"
          v-model="checkoutFieldsResult['companyEmail']"
          data-testid="co-email"
          autocomplete="off"
        />
      </v-col>
      <!--
        Client Portal v2 (PR C): 0 / 1 / N typed department picker.
        Shown ONLY when the store maps to 2+ client departments (the shopper
        MUST pick). 0 -> no prompt; 1 -> auto-attributed server-side (no
        prompt). Binds a typed client_department_id, replacing the legacy
        free-text checkout_fields["Department"] string.
      -->
      <v-col
        v-if="departmentPickerMode === 'required'"
        cols="12"
        md="12"
        class="py-0"
      >
        <v-select
          label="Department *"
          v-model="checkoutFieldsResult['selectedClientDepartmentId']"
          :items="clientDepartments"
          :item-title="(d) => d.name"
          :item-value="(d) => d.id"
          data-testid="co-department"
        />
      </v-col>
      <v-col
        v-if="storeResolvesDestination"
        cols="12"
        md="12"
        class="py-0 mb-3"
      >
        <cart-ship-to-destination
          :ship-to="storeShipTo"
          :state="storeShipToState"
        />
      </v-col>
      <v-col
        v-if="
          campaign.checkout_fields.includes('Location') &&
            campaign.locations.length > 0 &&
            !localShipToShopperAddress
        "
        cols="12"
        md="12"
        class="py-0"
      >
        <v-select
          v-show="campaign.locations.length > 0"
          label="Location *"
          v-model="checkoutFieldsResult['selectedLocation']"
          :items="campaign.locations"
          :item-title="(l) => l.nickname"
          return-object
          @update:model-value="changedCompanyLocation"
          data-testid="co-location"
        />
      </v-col>
      <v-col
        v-if="
          campaign.checkout_fields.includes('Location') &&
            campaign.locations.length > 0 &&
            campaign.checkout_fields.includes('Shopper Location')
        "
        cols="12"
        md="12"
        class="py-0"
      >
        <v-switch
          label="Enter Alternate Shipping Address"
          v-model="localShipToShopperAddress"
          :disabled="salesTaxLoading"
          data-testid="co-shopper-location-switch"
        />
      </v-col>
      <v-col
        v-if="
          campaign.checkout_fields.includes('Shopper Location') &&
            localShipToShopperAddress
        "
      >
        <cart-saved-address-picker
          @select="onSavedAddressSelected"
        />
        <cart-shipping-form
          :campaign="campaign"
          :modelValue="shopperLocation"
          :shipToShopperAddress="localShipToShopperAddress"
          :countriesAndCodes="countriesAndCodes"
          :salesTaxLoading="salesTaxLoading"
          @form-valid-changed="onShippingFormValidChanged"
        />
      </v-col>
      <v-col
        v-if="
          campaign.checkout_fields.includes('Manager') &&
            campaign.managers.length > 0
        "
        cols="12"
        md="12"
        class="py-0"
      >
        <v-select
          label="Manager *"
          v-model="checkoutFieldsResult['selectedManager']"
          :items="campaign.managers"
          :item-title="(m) => m.first_name + ' ' + m.last_name"
          return-object
          data-testid="co-manager"
        />
      </v-col>
      <v-col cols="12" md="12" class="pt-0">
        <v-text-field
          v-for="(field, i) in customCheckoutFields"
          v-model="customCheckoutFieldsResult[field]"
          :key="field + i"
          :label="field + ' *'"
          data-testid="co-identifier"
          :rules="[validateNotEmpty]"
        />
      </v-col>
      <v-col cols="12" md="12" class="pt-0">
        <v-form v-model="costomDropdownsValid">
          <v-select
            v-for="(field, i) in checkoutCustomDropdowns"
            :key="field + i"
            :label="field['name']"
            v-model="customCheckoutFieldsResult[field['name']]"
            :value="customCheckoutFieldsResult[field['name']]"
            :items="field['options']"
            data-testid="co-identifier"
            :rules="[validateNotEmpty]"
          />
        </v-form>
      </v-col>
      <v-col
        v-if="showCouponField"
        cols="12"
        md="12"
        class="py-0 d-flex align-center"
      >
        <v-text-field
          label="Coupon *"
          v-model="localCoupon"
          data-testid="co-coupon"
          autocomplete="off"
          @keyup="onCouponKeyup"
        />
        <v-btn
          @click="applyCoupon"
          size="small"
          :variant="!couponEnteredButNotApplied ? 'flat' : 'elevated'"
          color="primary"
          elevation="2"
          class="btn-coupon"
          :disabled="applyCouponDisabled"
          data-testid="co-apply-coupon"
          style="text-transform: none"
        >
          <span>Apply Coupon</span>
        </v-btn>
        <v-icon
          v-if="couponAmount > 0"
          color="green-darken-2"
          class="justify-start"
          >done</v-icon
        >
      </v-col>
    </v-row>
  `,
};

// The body of `payNow`. Runs inside payNow's try/finally; `this` is the component.
async function payNowImpl() {
  this.stripePending = true;
  const fullName =
    this.localCheckoutFields.firstName +
    " " +
    this.localCheckoutFields.lastName;
  const description =
    "Store: " +
    this.campaign.name.replaceAll("/", "-") +
    " Name: " +
    fullName.replaceAll("/", "-");

  const amount = currency(this.cartCharge);
  const tax_colleted = currency(this.cartSalesTax);

  // Order pre-create-before-charge (Approach A): create the Order at
  // status payment_pending BEFORE charging the card. The card is then
  // charged AGAINST this order (order_id in the PaymentIntent) so the
  // backend recomputes the authoritative amount from the frozen order and
  // no charge can exist without a matching order. If pre-create fails
  // (409 staleness routed to the dialog by createPendingOrder, or an
  // error), abort BEFORE any charge -- no card is touched.
  let pendingOrderId = null;
  let deferredPayment = false;
  if (this.createPendingOrder) {
    const pending = await this.createPendingOrder();
    // Accepts both shapes: the object the server answers with today, and a
    // bare id, so an older caller that has not been updated still works
    // rather than silently losing the order it just created.
    pendingOrderId =
      pending && typeof pending === "object" ? pending.id : pending;
    deferredPayment = !!(
      pending &&
      typeof pending === "object" &&
      pending.deferred_payment
    );
    if (!pendingOrderId) {
      this.stripePending = false;
      this.$emit("payment-failed", { status: "pre_create_failed" });
      return;
    }
    // A RETRY OF AN ATTEMPT THAT ALREADY WENT THROUGH (Bug Intake #637): the
    // server answered with that attempt's placed order and StorefrontCart
    // has sent the shopper to its confirmation. Touch no card -- a second
    // vault or charge is exactly the duplicate this guards against.
    if (pending && typeof pending === "object" && pending.alreadyPlaced) {
      this.stripePending = false;
      this.localCcDialog = false;
      return;
    }
  }

  // DEFERRED (BATCHED) CHECKOUT: save the card, charge NOTHING.
  //
  // A batched order is charged when its batch RELEASES, and the window
  // between is what the whole batching feature exists for -- it is when
  // the order can still be edited. Charging here would collect money for a
  // line that is about to change, and (because release charges again) bill
  // the shopper twice.
  //
  // The SERVER decides which flow runs. ``deferred_payment`` comes from
  // ``create_pending_order``, which assigned the order's status; the
  // client cannot infer it, since batch-open is fail-open and an order on
  // a batching store may legitimately end up unbatched.
  if (deferredPayment) {
    const saved = await this.saveCardForBatch(pendingOrderId, fullName);
    this.stripePending = false;
    // Closed only once the card is saved. On a failure the dialog stays
    // open with the card as typed and the error showing, so the shopper
    // can correct it and press Place Order again -- the same as the
    // immediate path, which already returned before closing.
    if (saved) {
      this.localCcDialog = false;
    }
    return;
  }

  let result;
  try {
    result = await this.$axios.post("/stripe/createPaymentIntent", {
      payment_method_types: this.paymentMethodType,
      amount: amount.intValue,
      currency: this.currency,
      application_fee_amount: this.appFee.intValue,
      description: description,
      tax_colleted: tax_colleted.intValue,
      // Server-authoritative amount (Trello mG6Qd7nY): with an order_id
      // present the backend recomputes the charge + application fee from
      // the order's checkout-frozen money columns and IGNORES the client
      // amount above. Absent (legacy) it charges the client amount.
      order_id: pendingOrderId,
    });
  } catch (err) {
    // The capability gate (PR #3364) refuses with 409
    // `stripe_account_not_ready` when the DISTRIBUTOR's Connect account
    // cannot take card payments. Without this branch the generic
    // extractor falls through to `data.error` -- its documented last
    // candidate -- and shows the shopper the raw token, which is
    // unintelligible to them and addressed to somebody else entirely.
    if (isAccountNotReady(err)) {
      store.dispatch("raiseError", accountNotReadyMessage(err));
    } else if (isOrderNotPayable(err)) {
      store.dispatch("raiseError", orderNotPayableMessage());
    } else {
      store.dispatch("raiseError", err);
    }
    this.stripePending = false;
    this.$emit("payment-failed", err);
    return;
  }

  if (result.data.error) {
    store.dispatch("raiseError", result.data.error);
    this.stripePending = false;
    this.$emit("payment-failed", result.data.error);
    return;
  }
  if (result.data.message && result.data.message.error) {
    store.dispatch("raiseError", "Invalid account error");
  }
  const client_secret = result.data.client_secret;

  if (client_secret) {
    const newStripeEvent = StripeEvent.generateEmpty();
    newStripeEvent.amount_charged = amount.intValue;
    newStripeEvent.app_fee = this.appFee.value;
    newStripeEvent.processing_fee = this.cartProcessingFee.value;

    // BRIDGE TO THE CONNECTED ACCOUNT.
    //
    // The card Elements belong to the PLATFORM instance (see
    // `initStripeElements`), but this PaymentIntent was created with
    // stripe_account=<connected>, so it must be confirmed on
    // `this.stripe`. An Element cannot cross instances -- a single-use
    // token can, and is the documented way to present the same card to a
    // different account.
    const cardToken = await this.tokenizeCard(fullName);
    if (!cardToken) {
      // `tokenizeCard` has already surfaced the error and emitted
      // payment-failed. Returning here leaves the PaymentIntent
      // unconfirmed, which is the safe direction: nothing is charged and
      // the shopper can retry.
      this.stripePending = false;
      return;
    }

    const confirmResult = await this.stripe.confirmCardPayment(client_secret, {
      payment_method: {
        card: { token: cardToken },
        billing_details: {
          name: fullName,
        },
      },
    });
    if (confirmResult.error) {
      // Show error to customer (e.g., insufficient funds)
      // Card fields kept, as on the batched path (saveCardForBatch).
      store.dispatch("raiseError", confirmResult.error.message);
      this.stripePending = false;
      // Store declined info in the stripe_event table
      newStripeEvent.order_id = null;
      newStripeEvent.stripe_charge_id = client_secret.split("_secret_")[0];
      newStripeEvent.status = "charge_declined";
      // MUST NOT block the decline reaching the shopper. This POST 403s
      // for every shopper (/api/stripe_events is gated on
      // `superadmin.settings`; no shopper role holds it), and as an
      // unguarded await it aborted before the $emit below -- so a declined
      // card was never surfaced. See StorefrontCart.vue for the same fix on
      // the success path.
      try {
        await this.$store.dispatch("jv/post", [
          newStripeEvent,
          { url: "/stripe_events" },
        ]);
      } catch (e) {
        console.warn("stripe_events write failed (non-blocking):", describeError(e));
      }
      this.$emit("payment-failed", confirmResult.error);
      return;
    } else {
      // The payment has been processed!
      if (confirmResult.paymentIntent.status === "succeeded") {
        store.dispatch("raiseSuccess", "Payment Verified");
        // Emit to parent to FINALIZE the pre-created order (Approach A):
        // pass the pre-created orderId so handlePaymentComplete calls the
        // checkout(orderId) finalizer rather than building a fresh order.
        this.$emit("payment-complete", {
          paymentIntent: confirmResult.paymentIntent,
          stripeEvent: newStripeEvent,
          orderId: pendingOrderId,
        });
      } else if (
        confirmResult.paymentIntent.status === "requires_payment_method"
      ) {
        store.dispatch(
          "raiseError",
          "Sorry, please try again with a different payment method."
        );
        newStripeEvent.status = "charge_failed";
        // Guarded for the same reason as the declined path above.
        try {
          await this.$store.dispatch("jv/post", [
            newStripeEvent,
            { url: "/stripe_events" },
          ]);
        } catch (e) {
          console.warn("stripe_events write failed (non-blocking):", describeError(e));
        }
        this.$emit("payment-failed", { status: "requires_payment_method" });
      } else if (confirmResult.paymentIntent.status === "canceled") {
        store.dispatch("raiseError", "Your order has been canceled.");
        newStripeEvent.status = "charge_canceled";
        // Guarded for the same reason as the declined path above.
        try {
          await this.$store.dispatch("jv/post", [
            newStripeEvent,
            { url: "/stripe_events" },
          ]);
        } catch (e) {
          console.warn("stripe_events write failed (non-blocking):", describeError(e));
        }
        this.$emit("payment-failed", { status: "canceled" });
      }
    }
  }
  this.stripePending = false;
  this.localCcDialog = false;
}

export default {
  name: "CartCheckout",

  components: {
    "cart-shipping-form": CartShippingForm,
    "checkout-fields-form": CheckoutFieldsForm,
    "store-email-verification": StoreEmailVerification,
    "net-checkout-credential": NetCheckoutCredential,
  },

  mixins: [helpers, rules],

  props: {
    // A coupon lookup is in flight in the cart; passed to the form so Apply
    // Coupon stays disabled until it settles.
    couponChecking: { type: Boolean, default: false },
    campaign: { type: Object, required: true },
    checkoutFieldsResult: { type: Object, required: true },
    customCheckoutFieldsResult: { type: Object, required: true },
    customCheckoutFields: { type: Array, default: () => [] },
    checkoutCustomDropdowns: { type: Array, default: () => [] },
    coupon: { type: String, default: "" },
    couponAmount: { type: Number, default: 0 },
    couponType: { type: String, default: "%" },
    isPayroll: { type: Boolean, default: false },
    // The card-gating flag -- StorefrontCart.vue's isCard, the component's
    // own derivation of payment_method === "Individual CC Payment".
    // Deliberately a separate prop from isPayroll: a store with "Accept
    // Shopper Payments" OFF (empty payment_method) is neither card nor
    // payroll, and reading !isPayroll as "is card" opened a Stripe dialog
    // with no connected account for such a store.
    isCard: { type: Boolean, default: false },
    // THE breakdown (`checkout_quote.totals`) for the cart's CURRENT inputs,
    // or null while there is none. When present the payment dialog shows
    // the server's figures and its Total is what the card is charged; see
    // cartTotalsDisplay.js (Bug Intake #481).
    serverTotals: { type: Object, default: null },
    isEmailConfirmed: { type: Boolean, default: false },
    panel1: { type: Array, default: () => [true] },
    panel2: { type: Array, default: () => [false] },
    ccDialog: { type: Boolean, default: false },
    // Order pre-create-before-charge (Approach A): an async function
    // (owned by StorefrontCart) that creates the Order at status
    // payment_pending BEFORE the card is charged and resolves to its id.
    // payNow() calls it first and charges the card against the returned id.
    createPendingOrder: { type: Function, default: null },
    isCheckoutProcess: { type: Boolean, default: false },
    salesTaxLoading: { type: Boolean, default: false },
    calculatingShipping: { type: Boolean, default: false },
    placeOrderButtonDisabled: { type: Boolean, default: false },
    shipToShopperAddress: { type: Boolean, default: false },
    shopperLocation: { type: Object, required: true },
    countriesAndCodes: { type: Array, default: () => [] },
    // The server-resolved destination of a company / department store
    // (StorefrontCart's `storeShipTo`) and the quote's progress; rendered by
    // CheckoutFieldsForm as the "Shipping to" block.
    storeShipTo: { type: Object, default: null },
    storeShipToState: { type: String, default: "idle" },
    // Financial props for dialog summary
    total: { type: Number, default: 0 },
    cartTotal: { type: Number, default: 0 },
    subTotal: { type: Number, default: 0 },
    cartCharge: { type: Number, default: 0 },
    computedSalesTax: { type: Number, default: 0 },
    shippingCost: { type: Number, default: 0 },
    companyAllowance: { type: Number, default: 0 },
    // Highest-priced-item-free (https://trello.com/c/T756dpsG). Defaults to
    // 0 so every store that has not enabled the rule renders no row at all.
    // StorefrontCart already passed this down; until it was declared here
    // Vue parked it on $attrs and the dialog silently dropped the value.
    freeItemDiscount: { type: Number, default: 0 },
    cartProcessingFee: { type: Object, default: () => currency(0) },
    appFee: { type: Object, default: () => currency(0) },
    cartSalesTax: { type: Number, default: 0 },
    collectSalesTax: { type: Boolean, default: false },
    consolidateTaxFee: { type: Boolean, default: false },
    collectProcessingFee: { type: Boolean, default: false },
    showMinChargeText: { type: Boolean, default: false },
    isSalesTaxTBD: { type: Boolean, default: true },
    // "not-calculated" | "calculating" | "calculated" | "not-collected".
    // Replaces the bare "TBD" that rendered for three of those four states
    // indistinguishably. See the same prop on CartSummary.vue for the full
    // rationale -- including why "N/A" and "$0.00" are different answers.
    // The wording is deliberately identical between the two so the cart
    // summary and this checkout panel cannot disagree.
    salesTaxState: { type: String, default: "not-calculated" },
    isCartProcessingFeeTBD: { type: Boolean, default: false },
    isShippingCostTBD: { type: Boolean, default: true },
    cart: { type: Array, default: () => [] },
    // Stripe instance passed from parent (initialized in loadData), scoped to
    // the distributor's CONNECTED account. Used to confirm the PaymentIntent,
    // which the backend creates with stripe_account=<connected>.
    stripe: { type: Object, default: null },
    // Second parent-supplied instance, scoped to the PLATFORM account (loaded
    // with no `stripeAccount`). Used to confirm the SetupIntent, which the
    // backend creates on the platform. The two are NOT interchangeable: a
    // SetupIntent and its client_secret belong to exactly one account, and
    // confirming a platform SetupIntent on the connected instance returns
    // "No such setupintent" -- the live AdventHealth checkout failure.
    setupStripe: { type: Object, default: null },
    // Bumped by the parent's checkout staleness guard when the
    // admin changes payment_method mid-checkout. The watcher below
    // destroys mounted Stripe Elements so the existing localCcDialog
    // watcher will re-mount them next time the dialog opens.
    stripeRemountKey: { type: Number, default: 0 },
    // The ThreadFlow platform fee for this cart, in dollars. Passed in as a
    // FIGURE rather than recomputed from a rate, so the row cannot disagree
    // with the one CartSummary renders or with the charge.
    platformFee: { type: Number, default: 0 },
    // Whether the SHOPPER bears the platform fee (parent resolves it from
    // `stores.platform_fee_bearer`; only the exact string "distributor"
    // moves it off the shopper).
    //
    // DEFAULTS FALSE, deliberately the opposite of the backend's fail-safe.
    // See the identical prop on CartSummary.vue for the full rationale: a
    // `true` default would turn an unbound prop (the #3498 / #3549 defect,
    // which hit these very components) into ThreadFlow's cut printed on the
    // screen of a shopper whose distributor absorbs it. `false` degrades to
    // the pre-existing bug rather than inventing a worse one.
    shopperBearsPlatformFee: { type: Boolean, default: false },
  },

  emits: [
    "update:checkoutFieldsResult",
    "update:customCheckoutFieldsResult",
    "update:coupon",
    "update:couponAmount",
    "update:ccDialog",
    "update:isEmailConfirmed",
    "update:panel1",
    "update:panel2",
    "update:shipToShopperAddress",
    "update:isCartProcessingFeeTBD",
    "update:costomDropdownsValid",
    "email-confirmed",
    "location-changed",
    "place-order-clicked",
    "confirm-order",
    "apply-coupon",
    "payment-complete",
    "payment-failed",
    "form-valid-changed",
    // The address StoreEmailVerification just verified, or null when the
    // shopper edited it. The cart re-reads the allowance on it: on an
    // email-domain store the allowance belongs to the verified address
    // (card #596 follow-up).
    "email-verified",
  ],

  data() {
    return {
      // The address StoreEmailVerification last reported verified (lowercased),
      // or null. Read by `emailVerificationPending` (card nrNUCjgU).
      verifiedEmail: null,
      // TENDER (Unit 4, D-01). Credit Card is the DEFAULT -- not "whatever
      // was touched last", not "Net where available". A shopper who never
      // touches the tender control pays by card.
      //
      // Single-valued, which is what makes Card and Net MUTUALLY EXCLUSIVE
      // (BR-03, CK-05): a split tender is not merely discouraged here, it is
      // unrepresentable. Note that allowance / client budget are NOT values
      // of this field -- they are funding BANDS applied to the cart, so an
      // allowance-covered card order is still tender 'card'.
      selectedTender: "card",
      // What NetCheckoutCredential last reported: a held pass AND the terms
      // ticked (`ready`), and the terms version to send with the order.
      netCredentialReady: false,
      netTermsVersion: null,
      stripePending: false,
      card: null,
      cardNumber: null,
      elementsOrphaned: false,
      cardExpiry: null,
      cardCvc: null,
      costomDropdownsValid: true,
      shopperLocationFormValid: false,
      showNotificationDialog: false,
      notificationHeader: "",
      notificationBody: "",
      notificationBtn: "",
      domainIncorrectMessage:
        "Email address is not associated with an approved domain for this store. Please use a valid email address with an approved domain for this store",
      // VESTIGIAL -- read by nothing. This component never calls
      // `loadStripe`; it receives ready Stripe.js instances from
      // StorefrontCart as the `stripe` and `setupStripe` props, and those
      // are what carry the platform. Verified: this declaration is the only
      // occurrence of `stripePublishableKey` in the file, and the only one
      // in the repository outside the components that do load Stripe.js.
      //
      // Left in place rather than removed (deleting it is a separate
      // decision), but pointed at the shared helper so it cannot be mistaken
      // for a second, competing source of truth if someone does start
      // reading it.
      stripePublishableKey: buildTimePublishableKey(),
      paymentMethodType: "card",
      currency: "usd",
    };
  },

  computed: {
    /** The figures the payment dialog shows (server first, else estimate). */
    figures() {
      return cartDisplayFigures(this.serverTotals, {
        items: this.cartTotal + this.companyAllowance,
        salesTax: this.computedSalesTax,
        taxAndFees: this.consolidatedTaxAndFees.value,
        shipping: this.shippingCost,
        freeItem: this.freeItemDiscount,
        processingFee: this.cartProcessingFee.value,
        serviceFee:
          this.shopperBearsPlatformFee && this.platformFee > 0
            ? this.platformFee
            : 0,
        subtotal: this.subTotal,
        allowance: this.companyAllowance,
        total: this.cartCharge,
      });
    },

    /** The separate Processing Fee row: the server's when quoted. */
    showProcessingFeeRow() {
      if (this.campaign.consolidate_tax_fee) return false;
      return this.figures.fromServer
        ? this.figures.processingFee > 0
        : this.collectProcessingFee;
    },

    /**
     * The single consolidated "Tax and Fees" figure for the dialog.
     *
     * Sales tax + processing fee, plus the platform fee ONLY when the
     * shopper bears it -- the conditional addend that makes the consolidated
     * presentation foot, because the subtotal includes the platform fee on
     * exactly the same condition (utils/cartSubTotal.js).
     *
     * Uses `cartSalesTax`, which is what this template's consolidated row
     * already read; CartSummary's twin uses `computedSalesTax`. The two
     * surfaces keep their existing tax sources -- reconciling them is a
     * separate question from making the column add up, and changing one here
     * would alter a figure this change is not about.
     *
     * `appFee` is deliberately NOT summed: it is the legacy 3.95%
     * duplicating this same platform fee and is no longer charged.
     */
    consolidatedTaxAndFees() {
      // Re-wrapped in `currency()` rather than chaining off whatever
      // `.add()` returned -- `cartProcessingFee` is a prop and callers
      // supply plain objects whose `.add()` has no `.format()`. Depending on
      // a prop's return shape makes this money row throw a render error.
      const base = currency(
        this.cartProcessingFee.add(this.cartSalesTax).value
      );
      return this.shopperBearsPlatformFee ? base.add(this.platformFee) : base;
    },

    localCheckoutFields() {
      return this.checkoutFieldsResult;
    },
    /**
     * Place Order waits for a verified email (card nrNUCjgU): on an
     * email-gated payroll store the API refuses the order unless THIS
     * session verified the address the order carries. `requires_verified_
     * email` comes from the store read, single-sourced from the server's
     * own predicate; `verifiedEmail` is what StoreEmailVerification last
     * reported, so editing the address re-arms the gate.
     */
    emailVerificationPending() {
      if (!this.campaign?.requires_verified_email) return false;
      const typed = (this.localCheckoutFields?.companyEmail || "")
        .trim()
        .toLowerCase();
      return !typed || typed !== this.verifiedEmail;
    },
    localCustomCheckoutFields() {
      return this.customCheckoutFieldsResult;
    },
    localPanel1: {
      get() { return this.panel1; },
      set(val) { this.$emit("update:panel1", val); },
    },
    localPanel2: {
      get() { return this.panel2; },
      set(val) { this.$emit("update:panel2", val); },
    },
    localCcDialog: {
      get() { return this.ccDialog; },
      set(val) { this.$emit("update:ccDialog", val); },
    },
    localIsEmailConfirmed: {
      get() { return this.isEmailConfirmed; },
      set(val) { this.$emit("update:isEmailConfirmed", val); },
    },
    localCoupon: {
      get() { return this.coupon; },
      set(val) { this.$emit("update:coupon", val); },
    },
    localCouponAmount: {
      get() { return this.couponAmount; },
      set(val) { this.$emit("update:couponAmount", val); },
    },
    localShipToShopperAddress: {
      get() { return this.shipToShopperAddress; },
      set(val) { this.$emit("update:shipToShopperAddress", val); },
    },
    localCostomDropdownsValid: {
      get() { return this.costomDropdownsValid; },
      set(val) { this.costomDropdownsValid = val; },
    },

    /**
     * May this store OFFER Net Checkout as a tender?
     *
     * Reads the SERVER's answer (``campaign.net_checkout_available``, derived
     * from ``Store.net_checkout_available``: switch on, a Net PIN set,
     * distributor not suspended), never store settings directly. A
     * client-side copy of that rule would drift from the server's the first
     * time either changed, and would drift SILENTLY in the dangerous
     * direction.
     *
     * ``=== true`` rather than a truthiness check so a MISSING flag -- an
     * older or degraded API response -- reads as unavailable. Absent is not
     * permission; same fail-closed reasoning as the server-side predicate.
     *
     * Hiding the option is NOT the control. The server re-derives the same
     * predicate at order creation (and requires this session's net pass),
     * so a shopper who forges this flag gains nothing.
     */
    netCheckoutAvailable() {
      return this.campaign.net_checkout_available === true;
    },

    /**
     * Is the shopper paying by Net Checkout rather than by card?
     *
     * Guarded by ``netCheckoutAvailable`` so a store that stops offering Net
     * -- an admin edits the access mode while the cart is open -- cannot
     * leave a stale selection in force and post a tender the server will
     * refuse. The card path is always reachable, which is the safe fallback.
     */
    isNetCheckout() {
      return this.netCheckoutAvailable && this.selectedTender === "net_checkout";
    },

    /**
     * Max width of the Confirm Order dialog: 850 when the card form sits
     * beside the totals and tender columns, else a single 400px column.
     *
     * Was `showCCInfo ? 850 : 400`. Choosing Net Checkout hides the card form
     * (showCCInfo false) but the tender column stays, so the dialog dropped to
     * 400px with two columns inside it and the totals values wrapped one
     * character per line (Bug Intake 446 / 491). Width follows the columns
     * present, not the payment state.
     */
    dialogMaxWidth() {
      if (this.showCCInfo) return "850";
      return "400";
    },

    /**
     * Without the card form the dialog is ONE column (totals, then the tender
     * choice and note beneath). Side by side is only for the card view, where
     * the 850px dialog holds totals | tender | card form.
     */
    stackColumns() {
      return !this.showCCInfo;
    },

    showCCInfo() {
      // Net Checkout collects NO shopper card, so the card-entry form is
      // hidden for it. Asking for a card we will not charge would misrepresent
      // what is happening, and a shopper who typed one would reasonably
      // expect it to be used.
      if (this.isNetCheckout) return false;
      // Gated on whether the store CHARGES A CARD, not on whether it shows
      // prices. `storefront_pricing` is the "Show prices to shoppers" master
      // DISPLAY switch; it used to be a third disjunct here, which meant the
      // live AdventHealth store (payment_method='Individual CC Payment',
      // storefront_pricing NULL) opened the payment dialog with no card
      // panel. `isCard` is the component's derivation of
      // payment_method === "Individual CC Payment". Matches the identically
      // named flag in StorefrontCart.vue and the gate in
      // `openPaymentOrPlaceOrder` there -- a store that shows a card form
      // must ask for a card, and one that asks must have shown the form.
      // Deliberately NOT `!isPayroll`: a store with "Accept Shopper
      // Payments" OFF (empty payment_method) is neither card nor payroll.
      return this.isCard && this.cartCharge > 0;
    },
  },

  watch: {
    shopperLocationFormValid(newVal) {
      this.$emit("form-valid-changed", newVal);
    },

    // A remounted credential panel starts with no PIN pass read and the terms
    // unticked, so the parent must not keep the last "ready" it reported:
    // leaving Net, or a store change, drops it until the panel says so again.
    selectedTender(next) {
      if (next !== "net_checkout") this.resetNetCredential();
    },
    "campaign.id"() {
      this.resetNetCredential();
    },

    /**
     * Relay the custom-dropdown validity verdict up to StorefrontCart.
     *
     * The value originates in CheckoutFieldsForm, whose `<v-form>` wraps the
     * store's custom dropdowns. Before this hop existed the chain stopped
     * here: the child's emit was bound to this component's own local and
     * went no further, so StorefrontCart's copy of the flag kept its
     * `data()` initialiser (`true`) forever and its place-order gate on the
     * flag could never fire. A shopper could leave a required dropdown blank
     * and still check out.
     *
     * Mirrors the `form-valid-changed` relay directly above -- each
     * component in this chain watches its OWN copy and re-emits upward.
     */
    costomDropdownsValid(newVal) {
      this.$emit("update:costomDropdownsValid", newVal);
    },

    /**
     * When the ccDialog opens and Stripe is available, initialize Stripe
     * elements after the DOM has rendered.
     */
    localCcDialog(newVal) {
      // Vuetify unmounts the dialog body on close, which orphans the iframes
      // Stripe mounted into it. Drop the dead Elements now so the next open
      // (guarded on `!this.cardNumber` below) mounts fresh ones into the NEW
      // body. Without this a reopened dialog showed empty fields the shopper
      // could not type into.
      // Not while a submit is in flight: saveCardForBatch / tokenizeCard still
      // hold these Elements. The `stripePending` watcher finishes the job.
      if (!newVal && !this.stripePending) {
        this.teardownStripeElements();
      }
      // Closed mid-submit: the Elements live on, but their iframes sit in a body
      // Vuetify is about to unmount. Remember to rebuild them when the submit ends.
      if (!newVal && this.stripePending) {
        this.elementsOrphaned = true;
      }
      // Gated on whichever instance actually CREATES the Elements. Keying this
      // on `this.stripe` would leave the card form unmounted on a store whose
      // distributor has no connected account resolved, even though vaulting a
      // card (platform-scoped) does not need one.
      if (newVal && (this.setupStripe || this.stripe) && !this.cardNumber) {
        this.$nextTick(() => {
          this.initStripeElements();
        });
      }
    },

    /**
     * Checkout staleness guard (PR #920 P1 #1): when the parent
     * detects that the admin changed payment_method mid-checkout,
     * it bumps stripeRemountKey. We tear down the Stripe Elements
     * here so the localCcDialog watcher re-initializes them the
     * next time the dialog opens (with the new payment context).
     */
    stripePending(pending) {
      if (pending) return;
      if (!this.localCcDialog) {
        this.teardownStripeElements();
        this.elementsOrphaned = false;
      } else if (this.elementsOrphaned) {
        // Reopened while the submit was still running: the visible card fields
        // are dead iframes. Rebuild them into the current dialog body.
        this.elementsOrphaned = false;
        this.teardownStripeElements();
        this.$nextTick(() => {
          if (this.localCcDialog && (this.setupStripe || this.stripe)) {
            this.initStripeElements();
          }
        });
      }
    },

    stripeRemountKey(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.teardownStripeElements();
      }
    },
  },

  methods: {
    /** Destroy the mounted card Elements (if any) so they can be re-created. */
    teardownStripeElements() {
      for (const key of ["cardNumber", "cardExpiry", "cardCvc"]) {
        const el = this[key];
        if (el) {
          try {
            el.destroy();
          } catch (e) {
            // Already torn down with its DOM node; nothing left to release.
          }
        }
        this[key] = null;
      }
    },

    /**
     * StoreEmailVerification reported an address verified (or null: the
     * shopper edited it). Holds Place Order to exactly that address
     * (`emailVerificationPending`) and tells the cart, which re-reads the
     * allowance the verified address holds.
     */
    onEmailVerified(email) {
      this.verifiedEmail = email;
      this.$emit("email-verified", email);
    },

    editCompanyEmail(e) {
      this.localIsEmailConfirmed = false;
      this.localPanel1 = [false];
      this.localPanel2 = [false];
      this.$emit("update:isCartProcessingFeeTBD", true);
    },

    async confirmCompanyEmail() {
      this.localIsEmailConfirmed = true;
      this.$emit("update:isCartProcessingFeeTBD", false);
      this.localPanel1 = [true];
      this.localPanel2 = [true];

      // Allowance v1->v2 (Slice 1): the remaining allowance is sourced from
      // the v2 ``checkRemainedAllowance`` endpoint (granted - consumed on the
      // shopper's active grant), NOT the legacy ``campaign.company_allowance``
      // column. We always query v2 -- it returns 0 for stores with no active
      // allowance policy -- and drive the display + notification from that.
      // The endpoint reads g.account (the authenticated shopper), so no
      // company_email is required; it is left off deliberately.
      const response = await this.$axios.post("/order/checkRemainedAllowance", {
        campaign_id: this.campaign._jv.id,
      });
      const remainedAllowance = response.data.remained_allowance || 0;
      // Card #596 part D: the store's per-period allowance cap, not the
      // shopper's own balance, is what holds the figure down. The figure is
      // already capped by the server; this only explains it.
      const storeCapLimited = response.data.store_cap_limited === true;
      const storeCapNote = storeCapLimited
        ? " This is limited by your store's allowance for this period, " +
          "which is shared by every shopper."
        : "";

      if (remainedAllowance <= 0 && storeCapLimited) {
        this.notificationHeader = "Store Allowance Used Up";
        this.notificationBody =
          "Your store's allowance for this period has been used up, so no " +
          "store credit can be applied to this order.";
        this.notificationBtn = "Continue to Checkout";
        this.showNotificationDialog = true;
      } else if (remainedAllowance > 0) {
        this.notificationHeader = "Remaining Shopper Credits";
        if (this.isPayroll) {
          this.notificationBody =
            "You have $" +
            remainedAllowance.toFixed(2) +
            " of store credit available." +
            storeCapNote +
            " " +
            "This balance will be applied to this order. " +
            "Any order charges that exceed $" +
            remainedAllowance.toFixed(2) +
            " will be deducted from payroll as determined by your program administrator.";
        } else if (this.isCard) {
          this.notificationBody =
            "You have $" +
            remainedAllowance.toFixed(2) +
            " of store credit available." +
            storeCapNote +
            " " +
            "This balance will be applied to this order. " +
            "You will be prompted to enter a credit card for " +
            "any order charges that exceed $" +
            remainedAllowance.toFixed(2) +
            ".";
        } else {
          // Neither card nor payroll: "Accept Shopper Payments" is OFF
          // (empty payment_method). Checkout refuses this order outright
          // (PAYMENTS_OFF_MESSAGE) regardless of the allowance, so neither
          // the payroll nor the card copy above is true for this store.
          this.notificationBody =
            "You have $" +
            remainedAllowance.toFixed(2) +
            " of store credit available." +
            storeCapNote +
            " " +
            "This balance will be applied to this order.";
        }
        this.notificationBtn = "Great, Continue to Checkout";
        this.showNotificationDialog = true;
      }

      this.$emit("email-confirmed", {
        allowance: remainedAllowance,
        notification: this.showNotificationDialog
          ? {
              header: this.notificationHeader,
              body: this.notificationBody,
              btn: this.notificationBtn,
            }
          : null,
      });
    },

    getCompanyEmailRules() {
      return [
        this.validateNotEmpty,
        this.validateNotSpaces,
        this.validateEmail,
        this.validateEmailDomain,
      ];
    },

    validateEmailDomain(email) {
      if (
        this.campaign.is_restrict_email_domain &&
        this.campaign.email_domains &&
        this.campaign.email_domains.length
      ) {
        for (let i = 0; i < this.campaign.email_domains.length; i++) {
          if (
            this.validateSpecificEmailDomain(
              email,
              this.campaign.email_domains[i]
            ) === true
          ) {
            return true;
          }
        }
        return this.domainIncorrectMessage;
      } else {
        return true;
      }
    },

    /**
     * Initialize Stripe Elements and mount them to the DOM.
     * Called when the payment dialog opens.
     */
    initStripeElements() {
      // Mounted on the PLATFORM instance, not the connected one.
      //
      // Stripe.js binds an Element to the instance that created it, and
      // `confirmCardSetup` has no per-call account override -- so the Elements
      // must belong to whichever instance confirms. Which flow runs is the
      // SERVER's call (`deferred_payment`, decided per REQUEST because
      // batch-open is fail-open) and is not known until after the order is
      // pre-created, which is long after the card form has to be on screen.
      //
      // So the card is collected once, on the platform, and the CHARGE path
      // bridges to the connected account with a single-use token
      // (`tokenizeCard` below). A token crosses accounts; a PaymentMethod does
      // not -- verified against the live API, which rejects a platform-owned
      // PaymentMethod presented to a connected account with "OAuth key or
      // Stripe-Account header was used but API request was provided with a
      // platform-owned payment method ID". The alternative -- mounting on the
      // connected account and remounting once the flow is known -- would throw
      // away the card the shopper had already typed.
      const stripeForElements = this.setupStripe || this.stripe;
      if (!stripeForElements) return;

      var elements = stripeForElements.elements();

      // Floating labels
      var inputs = document.querySelectorAll(
        ".cell.stripe-div.stripe-form .input"
      );
      Array.prototype.forEach.call(inputs, function(input) {
        input.addEventListener("focus", function() {
          input.classList.add("focused");
        });
        input.addEventListener("blur", function() {
          input.classList.remove("focused");
        });
        input.addEventListener("keyup", function() {
          if (input.value.length === 0) {
            input.classList.add("empty");
          } else {
            input.classList.remove("empty");
          }
        });
      });

      var elementStyles = {
        base: {
          color: "#32325D",
          fontWeight: 500,
          fontFamily: "Source Code Pro, Consolas, Menlo, monospace",
          fontSize: "16px",
          fontSmoothing: "antialiased",

          "::placeholder": {
            color: "#CFD7DF",
          },
          ":-webkit-autofill": {
            color: "#e39f48",
          },
        },
        invalid: {
          color: "#E25950",

          "::placeholder": {
            color: "#FFCCA5",
          },
        },
      };

      var elementClasses = {
        focus: "focused",
        empty: "empty",
        invalid: "invalid",
      };

      this.cardNumber = elements.create("cardNumber", {
        style: elementStyles,
        classes: elementClasses,
      });
      this.cardNumber.mount("#stripe-form-card-number");

      this.cardExpiry = elements.create("cardExpiry", {
        style: elementStyles,
        classes: elementClasses,
      });
      this.cardExpiry.mount("#stripe-form-card-expiry");

      this.cardCvc = elements.create("cardCvc", {
        style: elementStyles,
        classes: elementClasses,
      });
      this.cardCvc.mount("#stripe-form-card-cvc");
    },

    async confirmPlaceOrder() {
      // NET CHECKOUT: no shopper card is collected, so nothing here touches
      // Stripe. The order is created with the deferred tender; the
      // decoration, platform fee and S&H are recorded against the frozen
      // order row and billed to the Distributor on the 1st/15th invoice
      // after the order ships (Jake, 2026-10-28). Nothing is collected here.
      //
      // Checked FIRST, before the payroll/zero-charge branch, because those
      // conditions are about whether a CARD is needed and this one is about
      // which tender was chosen. Falling through to `payNow` would create a
      // PaymentIntent for an order the shopper is explicitly not paying for.
      if (this.isNetCheckout) {
        await this.placeNetCheckoutOrder();
        return;
      }
      // The no-card branch. Deliberately the exact negation of `showCCInfo`
      // (minus the Net Checkout case handled above), so the dialog cannot
      // show a card form and then decline to charge it, or charge without
      // having shown one. `storefront_pricing` used to be a third disjunct
      // here: on the live AdventHealth store that made this emit
      // `confirm-order` for a $99.00 card order, creating it with no
      // PaymentIntent.
      if (!this.isCard || this.cartCharge <= 0) {
        this.$emit("confirm-order");
      } else {
        await this.payNow();
      }
    },

    /**
     * Create the order on the Net Checkout tender. Charges NOTHING.
     *
     * The absence of every Stripe call is the point, not an omission: no
     * PaymentIntent (nothing is being charged now) and no SetupIntent (no
     * card is being vaulted for later either -- collection is by invoice to
     * the Distributor on the 1st/15th, which is Unit 7 and not the shopper's
     * concern).
     *
     * The tender is passed to the SERVER, which re-derives whether the
     * store offers Net and requires this session's net pass, refusing
     * otherwise. This client-side branch is convenience; the refusal is the
     * control.
     */
    resetNetCredential() {
      this.netCredentialReady = false;
      this.netTermsVersion = null;
    },

    onNetCredentialState({ ready, termsVersion }) {
      this.netCredentialReady = ready === true;
      this.netTermsVersion = termsVersion || null;
    },

    async placeNetCheckoutOrder() {
      if (!this.createPendingOrder) {
        // No pre-create path wired (admin / legacy caller). Fall back to the
        // single-shot confirm-order emit, which carries the tender the same
        // way -- rather than silently dropping the order on the floor.
        this.$emit("confirm-order", { tender: "net_checkout" });
        this.localCcDialog = false;
        return;
      }

      this.stripePending = true;
      const pending = await this.createPendingOrder({
        tender: "net_checkout",
        netTermsVersion: this.netTermsVersion,
      });
      this.stripePending = false;

      // The order build refused the Net credential: answer IN PLACE (cart and
      // form untouched, dialog stays open) rather than raising an error.
      if (pending && typeof pending === "object" && pending.netRefusal) {
        await this.answerNetRefusal(pending.netRefusal);
        this.$emit("payment-failed", { status: pending.netRefusal });
        return;
      }

      const orderId =
        pending && typeof pending === "object" ? pending.id : pending;
      // Already placed by an earlier try of this attempt (Bug Intake #637):
      // its confirmation is showing; there is nothing to finish.
      if (pending && typeof pending === "object" && pending.alreadyPlaced) {
        this.localCcDialog = false;
        return;
      }
      if (!orderId) {
        // createPendingOrder already surfaced the error (or routed a 409 to
        // the staleness dialog). Emit so the parent leaves its processing
        // state, exactly as the card path does on a pre-create failure.
        this.$emit("payment-failed", { status: "pre_create_failed" });
        return;
      }

      this.localCcDialog = false;
      // Reuse the SHARED completion path rather than a Net-only one. The
      // parent's ``handlePaymentComplete`` finalizes via checkout(orderId),
      // clears the cart and navigates to the confirmation -- and it already
      // handles a completion with NO paymentIntent correctly (it writes no
      // stripe_events row, because that table records CHARGES, and it shows
      // no orphan-charge dialog, because no money moved). Those are exactly
      // Net Checkout's semantics, so a second handler would be a second copy
      // of the same reasoning waiting to drift.
      this.$emit("payment-complete", {
        orderId,
        paymentIntent: null,
        setupIntent: null,
        stripeEvent: null,
      });
    },

    /**
     * 403 net_pin_required: the pass expired or the PIN was rotated; the PIN
     * field comes back. 409 net_terms_stale / 400 net_terms_required: the
     * terms are refetched and asked again.
     */
    async answerNetRefusal(code) {
      const credential = this.$refs.netCredential;
      if (!credential) return;
      if (code === NET_PIN_REQUIRED_CODE) {
        credential.reopenPin();
      } else {
        await credential.reloadTerms();
      }
    },

    /**
     * Vault the shopper's card against a BATCHED order. Charges nothing.
     *
     * The deferred half of checkout (order batching,
     * https://trello.com/c/JhIG8nVU). The card is saved with a SetupIntent
     * (``usage="off_session"``) and charged by the backend when the batch
     * releases -- an off-session merchant-initiated charge, days later, with
     * the buyer absent.
     *
     * ``order_id`` is not optional. It is what makes the SERVER record which
     * SetupIntent belongs to which order, and the checkout finalizer refuses
     * to promote a batched order unless the id presented MATCHES that record.
     * Omitting it produces a vaulted card and a 402 at finalize.
     *
     * NO StripeEvent row is written here, deliberately: that table records
     * CHARGES, and nothing has been charged. ``batch_charge`` writes the
     * charge record when money actually moves.
     */
    async saveCardForBatch(pendingOrderId, fullName) {
      // Captured BEFORE the first await: this submit owns these Elements even
      // if the dialog is closed or reopened while createSetupIntent is in flight.
      const card = this.cardNumber;
      let result;
      try {
        result = await this.$axios.post("/stripe/createSetupIntent", {
          order_id: pendingOrderId,
        });
      } catch (err) {
        // 409 `order_not_payable`: the order is already finalized, placed or
        // canceled (usually completed in another tab). Say so in words.
        store.dispatch(
          "raiseError",
          isOrderNotPayable(err) ? orderNotPayableMessage() : err,
        );
        this.$emit("payment-failed", err);
        return false;
      }

      if (result.data.error) {
        store.dispatch("raiseError", result.data.error);
        this.$emit("payment-failed", result.data.error);
        return false;
      }

      // A REPLAYED SetupIntent that already succeeded (Bug Intake #637): the
      // same attempt asked again after a lost answer, and Stripe returned the
      // SetupIntent it created the first time (the attempt key is its
      // Idempotency-Key). The card is already vaulted, so confirming again
      // would fail; finish the order with it. The server re-verifies it
      // against Stripe and against the id it recorded before finalizing.
      if (result.data.status === "succeeded" && result.data.setup_intent_id) {
        this.$emit("payment-complete", {
          setupIntent: { id: result.data.setup_intent_id, status: "succeeded" },
          orderId: pendingOrderId,
        });
        return true;
      }

      const clientSecret = result.data.client_secret;
      if (!clientSecret) {
        // No secret means the server refused to link the SetupIntent to the
        // order. Confirming anything now would vault a card the finalizer can
        // never match, so the order could never be completed OR charged.
        store.dispatch("raiseError", "Your card could not be saved.");
        this.$emit("payment-failed", { status: "setup_intent_failed" });
        return false;
      }

      // PLATFORM instance, matching where the backend created the SetupIntent.
      // Confirming on `this.stripe` (connected) is the production 404.
      const setupStripe = this.setupStripe || this.stripe;
      if (!card) {
        store.dispatch(
          "raiseError",
          "Your card details were closed before they could be saved. Please try again."
        );
        this.$emit("payment-failed", { status: "card_element_missing" });
        return false;
      }
      let confirmResult;
      try {
        confirmResult = await setupStripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card,
            billing_details: { name: fullName },
          },
        });
      } catch (err) {
        // Stripe.js throws (rather than returning `.error`) for a card Element
        // that is gone. Fail cleanly so the caller resets `stripePending`.
        store.dispatch("raiseError", "Your card could not be saved. Please try again.");
        this.$emit("payment-failed", err);
        return false;
      }

      if (confirmResult.error) {
        // The card fields are NOT cleared: a decline, a wrong CVC or a failed
        // 3DS check is usually fixed by changing one field, and clearing made
        // every retry a full retype (AH event, 2026-10-06).
        store.dispatch("raiseError", confirmResult.error.message);
        this.$emit("payment-failed", confirmResult.error);
        return false;
      }

      const setupIntent = confirmResult.setupIntent;
      if (!setupIntent || setupIntent.status !== "succeeded") {
        // ``requires_payment_method`` is what Stripe reports after a decline
        // during setup. Reporting the order complete here would finalize it
        // with no chargeable card behind it -- it would reach a decorator and
        // could never be billed, with the failure surfacing days later at
        // release rather than now, while the shopper is here to fix it.
        store.dispatch(
          "raiseError",
          "Sorry, please try again with a different payment method."
        );
        this.$emit("payment-failed", {
          status: (setupIntent && setupIntent.status) || "setup_failed",
        });
        return false;
      }

      store.dispatch("raiseSuccess", "Card saved");
      // ``setupIntent`` rather than ``paymentIntent``: the parent threads this
      // id into the finalizer as the proof of a vaulted card, and the two
      // proofs are NOT interchangeable server-side.
      this.$emit("payment-complete", {
        setupIntent,
        orderId: pendingOrderId,
      });
      return true;
    },

    /**
     * Convert the mounted card Element into a single-use token.
     *
     * The one thing that can carry a card from the PLATFORM instance (which
     * owns the Elements) to the CONNECTED account (which owns the
     * PaymentIntent). Verified against the live API: a connected account
     * accepts `card[token]` from a platform-created token, while a
     * platform-owned PaymentMethod id is rejected outright.
     *
     * Returns the token id, or null when tokenization failed -- in which case
     * the error has already been surfaced and `payment-failed` emitted, so the
     * caller must simply stop. Returning null rather than throwing keeps the
     * failure on the same path as a decline: nothing charged, form re-enabled.
     */
    async tokenizeCard(fullName) {
      const stripeForElements = this.setupStripe || this.stripe;
      if (!stripeForElements || !this.cardNumber) {
        store.dispatch("raiseError", "Your card could not be read.");
        this.$emit("payment-failed", { status: "tokenize_failed" });
        return null;
      }

      let tokenResult;
      try {
        tokenResult = await stripeForElements.createToken(this.cardNumber, {
          name: fullName,
        });
      } catch (err) {
        store.dispatch("raiseError", err);
        this.$emit("payment-failed", { status: "tokenize_failed" });
        return null;
      }

      if (!tokenResult || tokenResult.error || !tokenResult.token) {
        // A card-level problem (invalid number, expired, failed Luhn) shows up
        // HERE now rather than at confirm time. Surfacing the Stripe message
        // keeps the shopper-visible text as specific as it was before.
        const message =
          (tokenResult && tokenResult.error && tokenResult.error.message) ||
          "Your card could not be read.";
        store.dispatch("raiseError", message);
        this.$emit("payment-failed", { status: "tokenize_failed" });
        return null;
      }

      return tokenResult.token.id;
    },

    /**
     * Every exit path (including a throw from createPendingOrder() or
     * Stripe.js) ends with `stripePending` false and the failed state shown,
     * so the X and Place Order are never left disabled until a reload.
     */
    async payNow() {
      this.stripePending = true;
      try {
        await payNowImpl.call(this);
      } catch (err) {
        store.dispatch("raiseError", err);
        this.$emit("payment-failed", { status: "pay_now_threw" });
      } finally {
        this.stripePending = false;
      }
    },
  },
};
</script>

<style scoped>
/* The Total amount must never wrap ("$135.6" / "0" at 360px, Bug Intake
   446/491); on narrow screens shrink it rather than break it. */
@media (max-width: 420px) {
  .co-total-value {
    font-size: 1rem;
  }
}
.textline {
  font-size: 18px;
}

.btn-coupon {
  max-width: 120px;
}

.panel-header {
  text-transform: uppercase;
  display: flex;
  align-content: center;
  font-weight: 500;
}

.panel-content {
  padding: 12px 24px;
}

.expansion-panel {
  border: 1px solid #e7e7e7;
  overflow: visible !important;
}

.expansion-panel :deep(.v-expansion-panel-text__wrapper) {
  overflow: visible !important;
}

.edit-text {
  display: flex;
  margin-right: 10px;
  align-items: center;
  color: #575757;
  justify-content: right;
}

.panel-header-layout {
  justify-content: space-between;
}
</style>

<!-- Stripe styles must be unscoped — Stripe injects iframes that scoped selectors cannot reach -->
<style>
/* Stripe credit card form style */
.stripe-div.stripe-form {
  background-color: #fff;
}

.stripe-div.stripe-form * {
  font-family: Source Code Pro, Consolas, Menlo, monospace;
  font-size: 16px;
  font-weight: 500;
}

.stripe-div.stripe-form .row {
  display: -ms-flexbox;
  display: flex;
  margin: 0 5px 10px;
}

.stripe-div.stripe-form .field {
  position: relative;
  width: 100%;
  height: 50px;
  margin: 0 10px;
}

.stripe-div.stripe-form .field.half-width {
  width: 50%;
}

.stripe-div.stripe-form .field.quarter-width {
  width: calc(25% - 10px);
}

.stripe-div.stripe-form .baseline {
  position: absolute;
  width: 100%;
  height: 1px;
  left: 0;
  bottom: 0;
  background-color: #cfd7df;
  transition: background-color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.stripe-div.stripe-form label {
  position: absolute;
  width: 100%;
  left: 0;
  bottom: 8px;
  color: #cfd7df;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transform-origin: 0 50%;
  cursor: text;
  pointer-events: none;
  transition-property: color, transform;
  transition-duration: 0.3s;
  transition-timing-function: cubic-bezier(0.165, 0.84, 0.44, 1);
}

.stripe-div.stripe-form .input {
  position: absolute;
  width: 100%;
  left: 0;
  bottom: 0;
  padding-bottom: 7px;
  color: #32325d;
  background-color: transparent;
}

.stripe-div.stripe-form .input::-webkit-input-placeholder {
  color: transparent;
  transition: color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.stripe-div.stripe-form .input::-moz-placeholder {
  color: transparent;
  transition: color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.stripe-div.stripe-form .input:-ms-input-placeholder {
  color: transparent;
  transition: color 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.stripe-div.stripe-form .input.StripeElement {
  opacity: 0;
  transition: opacity 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  will-change: opacity;
}

.stripe-div.stripe-form .input.focused,
.stripe-div.stripe-form .input:not(.empty) {
  opacity: 1;
}

.stripe-div.stripe-form .input.focused::-webkit-input-placeholder,
.stripe-div.stripe-form .input:not(.empty)::-webkit-input-placeholder {
  color: #cfd7df;
}

.stripe-div.stripe-form .input.focused::-moz-placeholder,
.stripe-div.stripe-form .input:not(.empty)::-moz-placeholder {
  color: #cfd7df;
}

.stripe-div.stripe-form .input.focused:-ms-input-placeholder,
.stripe-div.stripe-form .input:not(.empty):-ms-input-placeholder {
  color: #cfd7df;
}

.stripe-div.stripe-form .input.focused + label,
.stripe-div.stripe-form .input:not(.empty) + label {
  color: #aab7c4;
  transform: scale(0.85) translateY(-25px);
  cursor: default;
}

.stripe-div.stripe-form .input.focused + label {
  color: #24b47e;
}

.stripe-div.stripe-form .input.invalid + label {
  color: #ffa27b;
}

.stripe-div.stripe-form .input.focused + label + .baseline {
  background-color: #24b47e;
}

.stripe-div.stripe-form .input.focused.invalid + label + .baseline {
  background-color: #e25950;
}

.stripe-div.stripe-form input,
.stripe-div.stripe-form button {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  outline: none;
  border-style: none;
}

.stripe-div.stripe-form input:-webkit-autofill {
  -webkit-text-fill-color: #e39f48;
  transition: background-color 100000000s;
  -webkit-animation: 1ms void-animation-out;
}

.stripe-div.stripe-form .StripeElement--webkit-autofill {
  background: transparent !important;
}

.stripe-div.stripe-form input,
.stripe-div.stripe-form button {
  -webkit-animation: 1ms void-animation-out;
}

.stripe-div.stripe-form button {
  display: block;
  width: calc(100% - 30px);
  height: 40px;
  margin: 40px 15px 0;
  background-color: #24b47e;
  border-radius: 4px;
  color: #fff;
  text-transform: uppercase;
  font-weight: 600;
  cursor: pointer;
}

.stripe-div.stripe-form .error svg {
  margin-top: 0 !important;
}

.stripe-div.stripe-form .error svg .base {
  fill: #e25950;
}

.stripe-div.stripe-form .error svg .glyph {
  fill: #fff;
}

.stripe-div.stripe-form .error .message {
  color: #e25950;
}

.stripe-div.stripe-form .success .icon .border {
  stroke: #abe9d2;
}

.stripe-div.stripe-form .success .icon .checkmark {
  stroke: #24b47e;
}

.stripe-div.stripe-form .success .title {
  color: #32325d;
  font-size: 16px !important;
}

.stripe-div.stripe-form .success .message {
  color: #8898aa;
  font-size: 13px !important;
}

.stripe-div.stripe-form .success .reset path {
  fill: #24b47e;
}
</style>
